/**
 * CharmHealth EHR Injector
 * Description: Integration for CharmHealth EHR SOAP note interface.
 *
 * Notes:
 * - CharmHealth uses a custom WYSIWYG editor ("ze") that renders content inside an iframe.
 *   Comprehend must set innerHTML of the iframe's body AND update the hidden VALUE input.
 * - Table cells use <textarea class="myTabletd mention"> elements.
 * - The per-entry checkbox (input[type="checkbox"]) is checked by Comprehend only when
 *   content is actually written to a free-text field, not during table writes.
 */

const SECTION_ID_MAP: Record<string, string> = {
  subjectiveDiv: 'Subjective',
  objectiveDiv: 'Objective',
  assessmentDiv: 'Assessment',
  planDiv: 'Plan'
};

var questions: any[] = [];
var questionInteractions: Record<string, any> = {};

/** Main Functions */

export function getPageInfo() {
  const hostname = window.location.hostname;
  const baseURL = hostname.split('.').slice(-2).join('.');
  const visitTypeEl = document.querySelector('#visittype_text') as HTMLElement;
  const noteType = visitTypeEl?.textContent?.trim() || 'SOAP Note';
  return { URL: baseURL, 'Note Type': noteType };
}

export function getPatientInfo() {
  const patientIdEl = document.querySelector('#PATIENT_ID') as HTMLInputElement;
  const emrID = patientIdEl?.value || null;

  const h1 = document.querySelector('#PatientHeader h1.v1-link') as HTMLElement;
  const fullName = (h1?.title || h1?.textContent || '').trim();
  const nameParts = fullName.split(' ');
  const firstName = nameParts[0] || null;
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : null;

  const dobEl = document.querySelector(
    '#patDashDateOfBirth strong'
  ) as HTMLElement;
  const dob = dobEl?.textContent?.replace(/ /g, '').trim() || null;

  return {
    'EMR ID': emrID,
    'First Name': firstName,
    'Last Name': lastName,
    DOB: dob
  };
}

export function getQuestions() {
  questions = [];
  questionInteractions = {};

  console.log('[CharmHealth] getQuestions: starting extraction');

  extractChiefComplaints();
  console.log(
    '[CharmHealth] extractChiefComplaints done. questionInteractions:',
    JSON.stringify(Object.keys(questionInteractions))
  );

  extractSOAPSections();
  console.log(
    '[CharmHealth] extractSOAPSections done. questionInteractions:',
    JSON.stringify(Object.keys(questionInteractions))
  );

  Object.keys(questionInteractions).forEach((key) => {
    const q = questionInteractions[key];
    console.log(
      `[CharmHealth] section "${key}" — type=${q.Type}, subsections:`,
      Object.keys(q.Structure || {})
    );
    if (q.Type === 'Flex' && Object.keys(q.Structure || {}).length === 0) {
      console.warn(`[CharmHealth] skipping empty Flex section: "${key}"`);
      return;
    }
    const question: Record<string, any> = { ...q, Question: key, Key: key };
    delete question.Elements;
    delete question.SubsectionElements;
    if (key === 'Objective') {
      question.Structure = { ...question.Structure, prune_early: true };
    }
    questions.push(question);
  });

  console.log(
    '[CharmHealth] getQuestions complete. questions:',
    JSON.stringify(
      questions.map((q) => ({
        Question: q.Question,
        Type: q.Type,
        subsections: Object.keys(q.Structure || {}).reduce(
          (acc: Record<string, string[]>, sub) => {
            acc[sub] = Object.keys(q.Structure[sub] || {});
            return acc;
          },
          {}
        )
      }))
    )
  );

  return questions;
}

/**
 * Subjective (S) section uses a different DOM pattern than O/A/P.
 * Chief Complaints lives here with its own hidden input (#CHIEF_COMPLAINTS)
 * and ze editor (#CHIEF_COMPLAINTS_DIV).
 */
function extractChiefComplaints() {
  const ccSection = document.getElementById('subjectiveDiv');
  if (!ccSection) return;

  const hiddenInput = document.getElementById(
    'CHIEF_COMPLAINTS'
  ) as HTMLInputElement;
  const ccDiv = document.getElementById('CHIEF_COMPLAINTS_DIV');
  const iframe = ccDiv?.querySelector('iframe.ze_area') as HTMLIFrameElement;
  if (!hiddenInput && !iframe) return;

  const sectionName = 'Subjective';
  questionInteractions[sectionName] = {
    Type: 'Flex',
    Key: sectionName,
    Question: sectionName,
    Structure: { 'Chief Complaints': {} },
    SubsectionElements: { 'Chief Complaints': ccSection },
    Elements: [ccSection]
  };

  questionInteractions[sectionName].Structure['Chief Complaints'][
    'Chief Complaints'
  ] = {
    'Existing Value': stripHtml(hiddenInput?.value || ''),
    Input: (val: string, replyType = 'overwrite') => {
      if (!val) return;
      const current = hiddenInput?.value || '';
      const next = replyType === 'append' ? current + val : val;
      if (hiddenInput) {
        hiddenInput.value = next;
        hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (iframe?.contentDocument?.body) {
        iframe.contentDocument.body.innerHTML = toHtml(next);
        iframe.contentDocument.body.dispatchEvent(
          new Event('input', { bubbles: true })
        );
      }
      if (next) {
        checkSectionCheckbox(ccSection);
      }
    }
  };
}

/**
 * Extracts Objective, Assessment, and Plan sections which all use the standard
 * `.eDrag.QuestempNotForComDiv` entry pattern with data-type="Table" or "Text Box".
 */
function extractSOAPSections() {
  for (const sectionId of Object.keys(SECTION_ID_MAP)) {
    const sectionEl = document.getElementById(sectionId);
    if (!sectionEl) continue;

    const sectionName = SECTION_ID_MAP[sectionId];

    // Walk span.v1-pg-t titles and entries together in DOM order so we can
    // assign each entry to the subsection title that precedes it.
    const nodes = Array.from(
      sectionEl.querySelectorAll('span.v1-pg-t, .eDrag.QuestempNotForComDiv')
    );
    if (nodes.length === 0) {
      console.warn(
        `[CharmHealth] "${sectionName}" (${sectionId}): no nodes found — section may be empty or use unexpected DOM structure`
      );
      continue;
    }
    console.log(`[CharmHealth] "${sectionName}": found ${nodes.length} nodes`);

    // Subjective may already exist (created by extractChiefComplaints) — merge
    // into it rather than overwriting so CC is preserved alongside the SOAP entries.
    if (!questionInteractions[sectionName]) {
      questionInteractions[sectionName] = {
        Type: 'Flex',
        Key: sectionName,
        Question: sectionName,
        Structure: {},
        SubsectionElements: {},
        Elements: [sectionEl]
      };
    }

    const subsectionCounts: Record<string, number> = {};
    let currentSubsectionTitle = '';

    nodes.forEach((node) => {
      if (node.tagName === 'SPAN' && node.classList.contains('v1-pg-t')) {
        currentSubsectionTitle = node.textContent?.trim() || '';
        return;
      }
      const dataType = node.getAttribute('data-type');
      if (dataType === 'Text Box') {
        extractWYSIWYGField(
          node as HTMLElement,
          sectionName,
          subsectionCounts,
          currentSubsectionTitle
        );
      } else if (dataType === 'Table') {
        extractTableFields(
          node as HTMLElement,
          sectionName,
          subsectionCounts,
          currentSubsectionTitle
        );
      } else {
        console.warn(
          `[CharmHealth] "${sectionName}": unhandled data-type="${dataType}" under subsection="${currentSubsectionTitle}" — entry skipped`
        );
      }
    });
  }
}

/**
 * Handles Text Box entries that use the ze WYSIWYG editor (iframe-based).
 * When no explicit v1-pg-t label precedes the entry, the label is read from the
 * nearest .wDrag section wrapper's own v1-pg-t heading in the DOM (e.g. "Treatment Notes").
 */
function extractWYSIWYGField(
  entry: HTMLElement,
  sectionName: string,
  subsectionCounts: Record<string, number>,
  subsectionTitle = ''
) {
  const iframe = entry.querySelector('iframe.ze_area') as HTMLIFrameElement;
  const hiddenInput = entry.querySelector(
    'input[name="VALUE"]'
  ) as HTMLInputElement;
  if (!iframe && !hiddenInput) return;

  const rawLabel =
    subsectionTitle ||
    (() => {
      const wrapper = entry.closest('.wDrag') as HTMLElement | null;
      if (wrapper) {
        const titleSpan = Array.from(
          wrapper.querySelectorAll('span.v1-pg-t')
        ).find((el) => !el.closest('.eDrag.QuestempNotForComDiv'));
        const text = titleSpan?.textContent?.trim();
        if (text) return text;
      }
      return 'Notes';
    })();

  // Append "Notes" unless the label already ends with it (e.g. "Assessment Notes" stays as-is).
  const resolvedLabel = /notes$/i.test(rawLabel)
    ? rawLabel
    : `${rawLabel} Notes`;

  // Each WYSIWYG gets its own subsection; subsequent entries sharing the same
  // base label are disambiguated with a numeric suffix.
  const wysiwygCountKey = `${resolvedLabel}::wysiwyg`;
  subsectionCounts[wysiwygCountKey] =
    (subsectionCounts[wysiwygCountKey] || 0) + 1;
  const subsectionLabel =
    subsectionCounts[wysiwygCountKey] > 1
      ? `${resolvedLabel} (${subsectionCounts[wysiwygCountKey]})`
      : resolvedLabel;

  if (!questionInteractions[sectionName].Structure[subsectionLabel]) {
    questionInteractions[sectionName].Structure[subsectionLabel] = {};
    questionInteractions[sectionName].SubsectionElements[subsectionLabel] =
      entry;
  }

  questionInteractions[sectionName].Structure[subsectionLabel][
    subsectionLabel
  ] = {
    'Existing Value': stripHtml(hiddenInput?.value || ''),
    Input: (val: string, replyType = 'overwrite') => {
      if (!val) return;
      const current = hiddenInput?.value || '';
      const next = replyType === 'append' ? current + val : val;
      setWYSIWYGContent(entry, next);
    }
  };
}

/**
 * Handles Table entries.
 *
 * Two table layouts:
 * 1. Header row (no textareas) + data rows → multi-col (e.g. ROM | Left | Right)
 *    Subsection key: first header cell label (e.g. "Cervical ROM")
 *    Field keys: "{rowLabel} - {colHeader}"
 *
 * 2. All rows are data rows → 2-col (label | textarea)
 *    Subsection key: "{precedingV1PgTTitle} - Table" (e.g. "Physical Examination - Table")
 *    Field keys: each row's label
 */
function extractTableFields(
  entry: HTMLElement,
  sectionName: string,
  subsectionCounts: Record<string, number>,
  subsectionTitle = ''
) {
  const table = entry.querySelector('table');
  if (!table) return;

  const rows = Array.from(table.querySelectorAll('tbody tr'));
  if (rows.length === 0) return;

  const firstRow = rows[0];
  const firstRowHasTextarea = firstRow.querySelector('textarea') !== null;

  let subsectionLabel: string;
  let dataRows: Element[];
  let colHeaders: string[] = [];

  if (!firstRowHasTextarea) {
    // First row is a header row — extract column headers for multi-col field keys
    const headerCells = Array.from(firstRow.querySelectorAll('td'));
    colHeaders = headerCells
      .slice(1)
      .map(
        (cell, i) =>
          cell.querySelector('label')?.textContent?.trim() || `Col${i + 1}`
      );
    dataRows = rows.slice(1);
  } else {
    // 2-col table: all rows are data rows
    dataRows = rows;
  }

  // Header tables use the first header cell as the subsection name (e.g. "Cervical ROM").
  // Headerless tables use the preceding section title suffixed with " - Table"
  // (e.g. "Physical Examination - Table") so they are clearly identifiable.
  if (!firstRowHasTextarea) {
    const headerCells = Array.from(firstRow.querySelectorAll('td'));
    subsectionLabel =
      headerCells[0]
        ?.querySelector('label')
        ?.textContent?.trim()
        .replace(/:$/, '') || 'Table';
  } else {
    subsectionLabel = subsectionTitle ? `${subsectionTitle} - Table` : 'Table';
  }

  // Deduplicate subsection label
  subsectionCounts[subsectionLabel] =
    (subsectionCounts[subsectionLabel] || 0) + 1;
  if (subsectionCounts[subsectionLabel] > 1) {
    subsectionLabel = `${subsectionLabel} (${subsectionCounts[subsectionLabel]})`;
  }

  if (!questionInteractions[sectionName].Structure[subsectionLabel]) {
    questionInteractions[sectionName].Structure[subsectionLabel] = {};
    questionInteractions[sectionName].SubsectionElements[subsectionLabel] =
      entry;
  }

  dataRows.forEach((row) => {
    const cells = Array.from(row.querySelectorAll('td'));
    if (cells.length === 0) return;

    const rowLabel =
      cells[0]?.querySelector('label')?.textContent?.trim().replace(/:$/, '') ||
      'Unknown';

    if (cells.length === 2) {
      // 2-col: label | textarea
      const textarea = cells[1]?.querySelector(
        'textarea'
      ) as HTMLTextAreaElement;
      if (!textarea) return;
      const capturedTextarea = textarea;
      questionInteractions[sectionName].Structure[subsectionLabel][rowLabel] = {
        'Existing Value': capturedTextarea.value,
        Input: (val: string, replyType = 'overwrite') => {
          if (!val) return;
          const current = capturedTextarea.value;
          const next =
            replyType === 'append' ? `${current}\n${val}`.trim() : val;
          setTableTextareaValue(capturedTextarea, next);
        }
      };
    } else if (cells.length >= 3) {
      // 3-col: label | left-textarea | right-textarea
      cells.slice(1).forEach((cell, colIdx) => {
        const textarea = cell.querySelector('textarea') as HTMLTextAreaElement;
        if (!textarea) return;
        const colHeader = colHeaders[colIdx] || `Col${colIdx + 1}`;
        const fieldKey = `${rowLabel} - ${colHeader}`;
        const capturedTextarea = textarea;
        questionInteractions[sectionName].Structure[subsectionLabel][fieldKey] =
          {
            'Existing Value': capturedTextarea.value,
            Input: (val: string, replyType = 'overwrite') => {
              if (!val) return;
              const current = capturedTextarea.value;
              const next =
                replyType === 'append' ? `${current}\n${val}`.trim() : val;
              setTableTextareaValue(capturedTextarea, next);
            }
          };
      });
    }
  });
}

// Converts plain-text newlines to <br> tags for HTML rendering.
// Skips strings that already contain HTML tags.
function toHtml(text: string): string {
  if (/<[a-z][\s\S]*>/i.test(text)) return text;
  return text.replace(/\n/g, '<br>');
}

// Strips HTML tags from a string and decodes &nbsp; to a space.
// Used when reading WYSIWYG field values for use as plain-text context.
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
}

// Handlers

/**
 * Finds and checks the section checkbox associated with a given entry element.
 * Searches inside the entry first, then falls back to the parent container,
 * covering both DOM layouts CharmHealth uses (checkbox inside vs. sibling of entry).
 */
function checkSectionCheckbox(context: HTMLElement) {
  const checkbox = (context.querySelector('input[type="checkbox"]') ||
    context.querySelector('input.v1-sn-list-cb') ||
    context.parentElement?.querySelector('input[type="checkbox"]') ||
    context.parentElement?.querySelector(
      'input.v1-sn-list-cb'
    )) as HTMLInputElement | null;

  console.log('[CharmHealth] checkSectionCheckbox — found:', checkbox);
  if (checkbox && !checkbox.checked) {
    checkbox.checked = true;
    checkbox.dispatchEvent(new Event('change', { bubbles: true }));
  }
}

/**
 * Sets a textarea value and dispatches the events CharmHealth needs to register the update.
 * Does NOT click the textarea (which would trigger makeEditable and check the section checkbox).
 */
function setTableTextareaValue(textarea: HTMLTextAreaElement, value: string) {
  textarea.value = value;
  textarea.dispatchEvent(
    new Event('input', { bubbles: true, cancelable: true })
  );
  textarea.dispatchEvent(
    new Event('change', { bubbles: true, cancelable: true })
  );
  const entry = textarea.closest('.eDrag.QuestempNotForComDiv') as HTMLElement;
  if (entry) checkSectionCheckbox(entry);
}

/**
 * Updates the ze WYSIWYG editor by writing to both the hidden VALUE input and the iframe body.
 * CharmHealth reads from the hidden input on save; the iframe keeps the visual state in sync.
 * Checks the entry's section checkbox when content is non-empty.
 */
function setWYSIWYGContent(entry: HTMLElement, htmlValue: string) {
  const hiddenInput = entry.querySelector(
    'input[name="VALUE"]'
  ) as HTMLInputElement;
  const iframe = entry.querySelector('iframe.ze_area') as HTMLIFrameElement;

  if (hiddenInput) {
    hiddenInput.value = htmlValue;
    hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
  }

  if (iframe?.contentDocument?.body) {
    iframe.contentDocument.body.innerHTML = toHtml(htmlValue);
    iframe.contentDocument.body.dispatchEvent(
      new Event('input', { bubbles: true })
    );
    // keyup nudges ze's internal state watchers
    iframe.contentDocument.body.dispatchEvent(
      new KeyboardEvent('keyup', { bubbles: true })
    );
  }

  if (htmlValue) {
    checkSectionCheckbox(entry);
  }
}

// Universal answer functions (same contract as all other EMR injectors)

export function answerQuestions(answers: any[]) {
  console.log(
    '[CharmHealth] answerQuestions called with:',
    JSON.stringify(answers)
  );
  const errors: any[] = [];
  answers.forEach((answer) => {
    const error = answerQuestion(
      answer['Question'],
      answer['Answer'],
      answer['Reply Type']
    );
    if (error) {
      errors.push({
        Question: answer['Question'],
        Answer: answer['Answer'],
        Error: error
      });
    }
  });
  console.error('Errors:', errors);
  return errors;
}

export async function answerQuestion(
  question: string,
  answer: any,
  replyType: string
) {
  if (questionInteractions[question] == null) {
    console.error('Question not found:', question);
    return 'Question not found';
  }

  if (questionInteractions[question]['Type'] === 'Flex') {
    return await answerFlexQuestion(question, answer, replyType);
  }

  if (questionInteractions[question]['Input']) {
    questionInteractions[question]['Input'](answer, replyType);
    return true;
  }

  if (Array.isArray(answer)) {
    const notFound: string[] = [];
    for (const a of answer) {
      if (a in questionInteractions[question]['Choices']) {
        questionInteractions[question]['Choices'][a](true);
      } else {
        notFound.push(a);
      }
    }
    if (notFound.length > 0) {
      console.error('Answers not found:', notFound);
      return 'The following answers were not found: ' + notFound;
    }
    return true;
  }

  if (answer in questionInteractions[question]['Choices']) {
    questionInteractions[question]['Choices'][answer]();
    return true;
  }

  console.error('Answer not found:', answer);
  return 'Answer not found';
}

async function answerFlexQuestion(
  question: string,
  answer: Record<string, Record<string, any>>,
  replyType = 'overwrite'
) {
  console.log(
    `[CharmHealth] answerFlexQuestion — question="${question}", replyType="${replyType}", answer:`,
    JSON.stringify(answer)
  );
  try {
    const errors: any[] = [];
    const questionObject = questionInteractions[question];
    if (!questionObject) {
      return { Question: question, Error: `Question "${question}" not found.` };
    }

    const structure = questionObject.Structure || {};

    for (const [fieldKey, fieldDetails] of Object.entries(answer)) {
      const field = structure[fieldKey];
      if (!field) {
        console.error(`Field "${fieldKey}" not found in Structure.`);
        errors.push({ Field: fieldKey, Error: 'Field not found in Structure' });
        continue;
      }

      for (const [subFieldKey, value] of Object.entries(
        fieldDetails as Record<string, unknown>
      )) {
        const valuesToInput =
          typeof value === 'string'
            ? value.split('$').map((v) => v.trim())
            : [value];

        for (const val of valuesToInput) {
          if (field[subFieldKey]?.Input) {
            console.log(
              `[CharmHealth] writing "${fieldKey}" > "${subFieldKey}":`,
              val
            );
            try {
              await field[subFieldKey].Input(val, replyType);
              await new Promise((resolve) => setTimeout(resolve, 100));
            } catch (err: any) {
              errors.push({
                Field: `${fieldKey}.${subFieldKey}`,
                Error: `Error applying value: ${err.message}`
              });
            }
          } else {
            console.warn(`Input not found for "${fieldKey}.${subFieldKey}"`);
            errors.push({
              Field: `${fieldKey}.${subFieldKey}`,
              Error: 'Input function not found'
            });
          }
        }
      }
    }

    if (errors.length > 0) {
      console.error('Errors occurred:', errors);
      return errors;
    }
    console.log(
      `[CharmHealth] answerFlexQuestion — "${question}" completed successfully`
    );
    return null;
  } catch (err: any) {
    console.error('Unexpected error:', err);
    return { Question: question, Error: `Unexpected error: ${err.message}` };
  }
}

export async function getLogFromInjector() {
  try {
    const htmlContent = document.documentElement.outerHTML;
    chrome.runtime.sendMessage({
      type: 'INJECTOR_ERROR',
      error: { htmlContent }
    });
    return { htmlContent };
  } catch (error) {
    console.error('Error fetching document:', error);
    return { error: String(error) };
  }
}
